import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Link2, RefreshCw, CheckSquare } from "lucide-react";
import api from "@/lib/api";
import JobsCostCodesTabs from "@/components/JobsCostCodesTabs";
import { toast } from "sonner";
import StatusBadge from "@/components/StatusBadge";
import CsvImportButton from "@/components/CsvImportButton";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const emptyForm = { code: "", name: "", address: "", customer_name: "", start_date: "" };

export default function Jobs() {
  const [jobs, setJobs] = useState([]);
  const [show, setShow] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [governed, setGoverned] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [showSelect, setShowSelect] = useState(false);
  const [selectable, setSelectable] = useState([]);
  const [checkedIds, setCheckedIds] = useState([]);
  const [savingSelection, setSavingSelection] = useState(false);
  const [selectSearch, setSelectSearch] = useState("");
  const navigate = useNavigate();

  const load = () => api.get("/contractor/jobs").then((r) => setJobs(r.data));
  const loadGovernance = () => api.get("/contractor/integrations/qbo/status").then((r) => setGoverned(r.data.governed)).catch(() => {});
  useEffect(() => { load(); loadGovernance(); }, []);

  const openNew = () => { setForm(emptyForm); setShow(true); };

  const create = async () => {
    if (!form.code || !form.name) return toast.error("Code and name required");
    try {
      await api.post("/contractor/jobs", form);
      toast.success("Job created");
      setShow(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create job");
    }
  };

  const syncNow = async () => {
    setSyncing(true);
    try {
      await api.post("/contractor/integrations/qbo/sync-inbound");
      toast.success("Synced with QuickBooks");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Sync failed");
    } finally {
      setSyncing(false);
    }
  };

  const openSelect = async () => {
    const { data } = await api.get("/contractor/integrations/qbo/select/jobs");
    setSelectable(data);
    setCheckedIds(data.filter((r) => r.selected).map((r) => r.qbo_id));
    setShowSelect(true);
  };

  const toggleChecked = (qboId) => setCheckedIds((ids) => ids.includes(qboId) ? ids.filter((i) => i !== qboId) : [...ids, qboId]);

  const saveSelection = async () => {
    setSavingSelection(true);
    try {
      const { data } = await api.post("/contractor/integrations/qbo/select/jobs", { qbo_ids: checkedIds });
      toast.success(`${data.selected} job(s) selected — ${data.created} newly added, ${data.closed} closed`);
      setShowSelect(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save selection");
    } finally {
      setSavingSelection(false);
    }
  };

  return (
    <div className="p-6 md:p-8" data-testid="jobs-page">
      <JobsCostCodesTabs />
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-extrabold tracking-tight">Jobs</h1>
          <p className="text-slate-500 text-sm mt-1">Every purchase must be tied to a job, change order, or non-job context.</p>
        </div>
        <div className="flex items-center gap-2">
          {governed ? (
            <button onClick={openSelect} data-testid="select-qbo-jobs-button" className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-sky-500 hover:bg-sky-600 text-white text-[13px] font-semibold">
              <CheckSquare size={14} /> Select QB jobs
            </button>
          ) : (
            <>
              <CsvImportButton entity="jobs" label="Jobs" onImported={load} />
              <button onClick={openNew} data-testid="new-job-button" className="flex items-center gap-2 px-4 py-2.5 rounded-lg bg-sky-500 hover:bg-sky-600 text-white text-[13px] font-semibold">
                <Plus size={14} /> New Job
              </button>
            </>
          )}
        </div>
      </div>

      {governed && (
        <div className="mt-5 flex items-center justify-between gap-3 flex-wrap text-[13px] bg-sky-50 border border-sky-100 rounded-lg px-3.5 py-3" data-testid="qbo-governed-banner">
          <div className="flex items-center gap-2 text-sky-900">
            <Link2 size={15} /> Managed by QuickBooks — jobs are mirrored from your QuickBooks customers/projects and selected above, not created here.
          </div>
          <button onClick={syncNow} disabled={syncing} data-testid="qbo-sync-now-button" className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-sky-200 text-[12px] font-semibold hover:bg-sky-100 disabled:opacity-60 shrink-0">
            <RefreshCw size={12} className={syncing ? "animate-spin" : ""} /> {syncing ? "Syncing…" : "Sync Now"}
          </button>
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="w-full mt-7 text-[13px]" data-testid="jobs-table">
          <thead>
            <tr className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold border-b border-slate-100">
              <th className="text-left py-2.5">Code</th><th className="text-left py-2.5">Name</th><th className="text-left py-2.5">Customer</th><th className="text-left py-2.5">Status</th>
            </tr>
          </thead>
          <tbody>
            {jobs.map((j) => (
              <tr key={j.id} onClick={() => navigate(`/contractor/jobs/${j.id}`)} className="cursor-pointer hover:bg-slate-50 border-b border-slate-50" data-testid={`job-row-${j.id}`}>
                <td className="py-3 mono text-sky-600 font-medium">{j.code}</td>
                <td className="py-3">{j.name}{j.source === "quickbooks" && <span className="ml-2 text-[10.5px] font-semibold text-sky-600 align-middle">QB</span>}</td>
                <td className="py-3 text-slate-500">{j.customer_name || "—"}</td>
                <td className="py-3"><StatusBadge status={j.status} /></td>
              </tr>
            ))}
            {jobs.length === 0 && (
              <tr><td colSpan={4} className="text-slate-400 text-sm text-center py-10">No jobs yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <Dialog open={show} onOpenChange={setShow}>
        <DialogContent data-testid="job-modal">
          <DialogHeader><DialogTitle>New Job</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-2">
            <input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} placeholder="Job code (e.g. BR-23005)" data-testid="job-code-input" className="w-full h-10 px-3 rounded-lg border border-slate-200 text-sm" />
            <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Job name" data-testid="job-name-input" className="w-full h-10 px-3 rounded-lg border border-slate-200 text-sm" />
            <input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} placeholder="Site address" data-testid="job-address-input" className="w-full h-10 px-3 rounded-lg border border-slate-200 text-sm" />
            <input value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} placeholder="Customer name (optional)" data-testid="job-customer-input" className="w-full h-10 px-3 rounded-lg border border-slate-200 text-sm" />
            <div>
              <label className="text-[12.5px] text-slate-500">Start date (optional)</label>
              <input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} data-testid="job-start-date-input" className="mt-1 w-full h-10 px-3 rounded-lg border border-slate-200 text-sm" />
            </div>
            <button onClick={create} data-testid="save-job-button" className="w-full h-10 rounded-lg bg-sky-500 text-white text-[13px] font-semibold">Create Job</button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={showSelect} onOpenChange={setShowSelect}>
        <DialogContent data-testid="select-jobs-modal">
          <DialogHeader><DialogTitle>Select QB jobs</DialogTitle></DialogHeader>
          <p className="text-[12.5px] text-slate-500 -mt-1">Only customers/projects active in QuickBooks are shown. Unchecking one closes its job here (history is kept).</p>
          <input value={selectSearch} onChange={(e) => setSelectSearch(e.target.value)} placeholder="Search customers and projects…" data-testid="select-job-search" className="mt-2 w-full h-9 px-2.5 rounded-lg border border-slate-200 text-[13px]" />
          <div className="mt-2 max-h-80 overflow-y-auto ss-scrollbar space-y-1.5">
            {selectable.filter((r) => !selectSearch || r.name.toLowerCase().includes(selectSearch.toLowerCase())).map((r) => (
              <label key={r.qbo_id} className="flex items-center gap-2.5 text-[13px] px-2.5 py-2 rounded-lg hover:bg-slate-50" data-testid={`select-job-row-${r.qbo_id}`}>
                <input type="checkbox" checked={checkedIds.includes(r.qbo_id)} onChange={() => toggleChecked(r.qbo_id)} data-testid={`select-job-checkbox-${r.qbo_id}`} />
                <span className="flex-1">{r.name}</span>
                {r.is_project && <span className="text-[10.5px] text-slate-400">project</span>}
                {r.has_ship_address && <span className="text-[10.5px] text-slate-400">has shipping address</span>}
              </label>
            ))}
            {selectable.length === 0 && <div className="text-slate-400 text-[12.5px] text-center py-6">No customers/projects synced yet — run Sync Now first.</div>}
          </div>
          <button disabled={savingSelection} onClick={saveSelection} data-testid="save-job-selection-button" className="w-full h-10 rounded-lg bg-sky-500 text-white text-[13px] font-semibold mt-3 disabled:opacity-60">
            {savingSelection ? "Saving…" : "Save Selection"}
          </button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
